import {
  Program,
  refScope,
  listStudyplanFieldsOption,
  listStudyplanStatusOption,
  AvailableContentItem,
  eocDraftPlanResponse,
  EocStudyPlanResponse,
  EocStudyPlansResponse,
  EocTeacherPlansResponse,
  EocAdminRelevantPlansResponse,
  CategorizedPlanAvailableContentItems,
  ProgramRefScopeInput,
  EocStudyPlanSettingsResponse,
  StudentMasteryLessonSnapsResponse,
  ResetRestoreMasteryLessonsRequest,
  StudentStudyPlanStatsInput,
  StudentStudyPlanStatsOutput
} from "../../../types/gqlTypes/graphql"
import {
  GET_DRAFT_PLAN,
  GET_CURRENT_PLAN_BY_REFSCOPE,
  GET_SECTION_AVAILABLE_ITEMS,
  LIST_STUDY_PLANS,
  GET_PLANS,
  GET_PLAN_HISTORY,
  GET_ITEM_PREVIEW,
  GET_ADMIN_RELEVANT_PLAN_INFO,
  GET_TEACHER_RELEVANT_PLAN_INFO,
  GET_PLAN_AVAILABLE_ITEMS,
  GET_ASSIGNED_SUBJECT_BY_REFSCOPE,
  GET_CUSTOM_PLAN_AT_REFSCOPE,
  GET_RECOMMENDED_PLAN_BY_REFSCOPE,
  GET_GLOBAL_PLAN_SETTINGS,
  GET_STUDENT_MASTERY_LESSON_SNAPS,
  GET_RESET_CLASS_MASTERY_LESSONS_AFFECTED_COUNT,
  GET_STUDENT_STUDY_PLAN_STATS
} from "./studyPlanQueries"

import { executeQuerySideload } from "../../generic-services/helpers/studyPlan"

// Resolver -> function to do the gql query and simply return the data. There's no post processing of the data in these functions
// NOTE: This file use a lot of types pre-defined in graphql.d.ts which copy from studyPlan backend

// Debug option. Don't use in prod
export async function listStudyPlansQuery(
  status: listStudyplanStatusOption,
  fields: listStudyplanFieldsOption
): Promise<JSON> {
  const response = await executeQuerySideload(LIST_STUDY_PLANS, {
    status,
    fields
  })
  return response.listStudyPlans
}

export async function getDraftPlan(
  planId: string
): Promise<eocDraftPlanResponse> {
  const response = await executeQuerySideload(GET_DRAFT_PLAN, {
    plan_id: planId
  })
  return response.getDraftPlan
}

export async function getCurrentPlanByRefScope(
  program: Program,
  subject: string,
  refScope: refScope,
  requirePercentSummary?: boolean
): Promise<EocStudyPlanResponse> {
  const response = await executeQuerySideload(GET_CURRENT_PLAN_BY_REFSCOPE, {
    program,
    subject,
    refScope,
    requirePercentSummary: requirePercentSummary ?? false
  })
  return response.getCurrentPlanByRefScope
}

export async function getRecommendedPlanByRefScope(
  program: Program,
  subject: string,
  refScope: refScope,
  requirePercentSummary?: boolean
): Promise<EocStudyPlanResponse> {
  const response = await executeQuerySideload(
    GET_RECOMMENDED_PLAN_BY_REFSCOPE,
    {
      program,
      subject,
      refScope,
      requirePercentSummary: requirePercentSummary ?? false
    }
  )
  return response.getRecommendedPlanByRefScope
}

// only return if that level have a custom plan. Consider using getCurrentPlan instead if you also want to get recommended plan.
export async function getCustomPlanAtRefScope(
  program: Program,
  subject: string,
  refScope: refScope,
  requirePercentSummary?: boolean
): Promise<EocStudyPlanResponse> {
  const response = await executeQuerySideload(GET_CUSTOM_PLAN_AT_REFSCOPE, {
    program,
    subject,
    refScope,
    requirePercentSummary: requirePercentSummary ?? false
  })
  return response.getCustomPlanAtRefScope
}

export async function getCurrentPlanForEachSubjectByRefScope(
  program: Program,
  refScope: refScope,
  requirePercentSummary?: boolean
): Promise<EocStudyPlansResponse> {
  const response = await executeQuerySideload(GET_PLANS, {
    program,
    refScope,
    requirePercentSummary: requirePercentSummary ?? false
  })
  return response.getCurrentPlanForEachSubjectByRefScope
}

export async function getSectionAvailableItems(
  planId: string,
  sectionId: string
): Promise<AvailableContentItem[]> {
  const response = await executeQuerySideload(GET_SECTION_AVAILABLE_ITEMS, {
    plan_id: planId,
    section_id: sectionId
  })
  return response.getSectionAvailableItems
}

// work with both draft and published plan
export async function getPlanAvailableItems(
  planId: string
): Promise<CategorizedPlanAvailableContentItems> {
  const response = await executeQuerySideload(GET_PLAN_AVAILABLE_ITEMS, {
    plan_id: planId
  })
  return response.getPlanAvailableItems
}

export async function getItemPreview(
  contentId: string,
  planId?: string,
  sectionId?: string
): Promise<AvailableContentItem> {
  const response = await executeQuerySideload(GET_ITEM_PREVIEW, {
    content_id: contentId,
    plan_id: planId,
    section_id: sectionId
  })
  return response.getItemPreview
}

export async function getPlanHistory(
  program: Program,
  subject: string,
  refScope: refScope
): Promise<string[]> {
  const response = await executeQuerySideload(GET_PLAN_HISTORY, {
    program,
    subject,
    refScope
  })
  return response.getPlanHistory
}

export async function getAdminRelevantPlanInfo(
  program: Program,
  subject: string,
  refScope: refScope
): Promise<EocAdminRelevantPlansResponse> {
  const response = await executeQuerySideload(GET_ADMIN_RELEVANT_PLAN_INFO, {
    program,
    subject,
    refScope
  })
  return response.getAdminRelevantPlanInfo
}

export async function getTeacherRelevantPlanInfo(
  program: Program,
  subject: string,
  refScope: refScope
): Promise<EocTeacherPlansResponse> {
  const response = await executeQuerySideload(GET_TEACHER_RELEVANT_PLAN_INFO, {
    program,
    subject,
    refScope
  })
  return response.getTeacherRelevantPlanInfo
}

export async function getAssignedSubjectsByRefScope(
  program: Program,
  refScope: refScope
): Promise<string[]> {
  const response = await executeQuerySideload(
    GET_ASSIGNED_SUBJECT_BY_REFSCOPE,
    {
      program,
      refScope
    }
  )
  //console.log(response.getAssignedSubjectsByRefScope)
  return response.getAssignedSubjectsByRefScope
}

export async function getGlobalPlanSettings(
  data: ProgramRefScopeInput
): Promise<EocStudyPlanSettingsResponse> {
  const response = await executeQuerySideload(GET_GLOBAL_PLAN_SETTINGS, {
    data
  })
  return response.getGlobalPlanSettings
}

// DF Disabled until further notice
// export async function getGuidPlanSettings(
//   data: PlanGuidMetaInput
// ): Promise<EocStudyPlanSettingsResponse> {
//   const response = await executeQuerySideload(GET_GUID_PLAN_SETTINGS, {
//     data
//   })
//   return response.getGuidPlanSettings
// }

export async function getStudentMasteryLessonSnaps(
  program: Program,
  subject: string,
  contentId: string,
  isFromExternalPage: boolean = false,
  assignmentId?: string
): Promise<StudentMasteryLessonSnapsResponse> {
  const response = await executeQuerySideload(
    GET_STUDENT_MASTERY_LESSON_SNAPS,
    {
      program,
      subject,
      contentId,
      contentLibrary: isFromExternalPage,
      assignmentId
    }
  )

  return response.getStudentMasteryLessonSnaps
}

export async function getResetClassMasteryLessonsAffectedCount(
  data: ResetRestoreMasteryLessonsRequest
): Promise<number> {
  const response = await executeQuerySideload(
    GET_RESET_CLASS_MASTERY_LESSONS_AFFECTED_COUNT,
    {
      data
    }
  )

  return response.getResetClassMasteryLessonsAffectedCount
}

export async function getStudentStudyPlanStats(
  data: StudentStudyPlanStatsInput
): Promise<StudentStudyPlanStatsOutput> {
  const response = await executeQuerySideload(GET_STUDENT_STUDY_PLAN_STATS, {
    data
  })

  return response.getStudentStudyPlanStats
}
